

































import { computed, defineComponent, onUnmounted, watch } from "@vue/composition-api";
import useBeforeUnloadTab from "@/hooks/useBeforeUnloadTab";
import useFullCreatorApplicationUpload, {
  FullCreatorApplicationUploadStatus,
} from "@/hooks/useFullCreatorApplicationUpload";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { PreventLogout } from "@/types";
import { EVENTS, PreventLogoutType } from "@/const";
import BaseIcon from "@/components/Base/BaseIcon";
import BaseButton from "@/components/BaseButton";
import BaseSpinner from "@/components/BaseSpinner";

export default defineComponent({
  name: "FullCreatorApplicationUpload",
  components: { BaseSpinner, BaseButton, BaseIcon },
  setup(_, { root }) {
    const {
      status,
      totalFiles,
      uploadedFilesNumber,
      isActive,
      retryApplicationSubmission,
      reset,
    } = useFullCreatorApplicationUpload();

    const visible = computed(
      () =>
        ![FullCreatorApplicationUploadStatus.IDLE, FullCreatorApplicationUploadStatus.RETRYING].includes(status.value)
    );
    const isComplete = computed(() => status.value === FullCreatorApplicationUploadStatus.SUCCESS);
    const hasUploadError = computed(() => status.value === FullCreatorApplicationUploadStatus.ERROR);
    const hasApplicationError = computed(() => status.value === FullCreatorApplicationUploadStatus.APPLICATION_ERROR);
    const hasError = computed(() => hasUploadError.value || hasApplicationError.value);
    const isWaitingForApplication = computed(
      () => status.value === FullCreatorApplicationUploadStatus.WAITING_FOR_APPLICATION
    );
    const isUploading = computed(() => status.value === FullCreatorApplicationUploadStatus.UPLOADING);
    const processingText = computed(() => {
      if (status.value === FullCreatorApplicationUploadStatus.PUBLISHING) {
        return $t("Publishing Full Creator application videos...");
      }
      if (status.value === FullCreatorApplicationUploadStatus.SUBMITTING) {
        return $t("Sending Full Creator application...");
      }
      return $t("Uploading Full Creator application videos...");
    });
    const errorText = computed(() =>
      hasApplicationError.value
        ? $t("Failed to send Full Creator application")
        : $t("Failed to upload Full Creator application videos")
    );

    const emitPreventLogout = (value: boolean) => {
      const preventLogout: PreventLogout = {
        type: PreventLogoutType.FULL_CREATOR_APPLICATION_UPLOAD,
        value,
      };
      if (value) {
        preventLogout.text = $t(
          "The application files are still uploading. The application won't be sent if you leave the website now."
        );
        preventLogout.cb = reset;
      }
      emitter.emit(EVENTS.PREVENT_LOGOUT, preventLogout);
    };

    const onRetryClick = () => {
      if (hasApplicationError.value) {
        retryApplicationSubmission();
        return;
      }
      root.$router.push({ name: "becomeCreator", query: { retryFullCreatorUpload: "1" } });
    };

    let successResetTimeout: ReturnType<typeof setTimeout> | null = null;
    watch(status, value => {
      if (successResetTimeout) clearTimeout(successResetTimeout);
      successResetTimeout = value === FullCreatorApplicationUploadStatus.SUCCESS ? setTimeout(reset, 2000) : null;
    });

    useBeforeUnloadTab(isActive);
    watch(isActive, emitPreventLogout, { immediate: true });
    onUnmounted(() => {
      if (successResetTimeout) clearTimeout(successResetTimeout);
      emitPreventLogout(false);
      reset();
    });

    return {
      visible,
      isComplete,
      hasError,
      isWaitingForApplication,
      isUploading,
      processingText,
      errorText,
      totalFiles,
      uploadedFilesNumber,
      onRetryClick,
    };
  },
});
